/**
 * Global notification helper — thin wrapper over react-toastify.
 *
 * Mount <ToastContainer /> once (see src/index.js). Use this helper everywhere
 * instead of page-level dismissible <Alert> banners or per-page react-bootstrap
 * <Toast> stacks, so success/error feedback is consistent app-wide.
 *
 * Usage:
 *   import notify from 'components/ui/feedback/notify';
 *   notify.success('Transaction saved');
 *   notify.error('Could not save the transaction');
 *   await notify.promise(saveFn(), { pending: 'Saving…', success: 'Saved', error: 'Failed' });
 */
import { toast } from 'react-toastify';

const baseOptions = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const withDefaults = (options) => ({ ...baseOptions, ...(options || {}) });

const notify = {
  success(message, options) {
    return toast.success(message, withDefaults(options));
  },
  error(message, options) {
    return toast.error(message, withDefaults({ autoClose: 6000, ...options }));
  },
  info(message, options) {
    return toast.info(message, withDefaults(options));
  },
  warning(message, options) {
    return toast.warning(message, withDefaults(options));
  },
  /**
   * Tie a toast lifecycle to a promise. Resolves/rejects pass through.
   */
  promise(promise, messages, options) {
    return toast.promise(promise, messages, withDefaults(options));
  },
  dismiss(toastId) {
    return toast.dismiss(toastId);
  },
};

export default notify;
export { notify };
